import React from 'react';
import { Link } from 'react-router-dom';

import { URL, EMAILS } from '../constants';

import { Event, EmailHubTemplate, SGTemplates } from '../types';
import { getUrl, isEmpty } from '../utils';

import { getTemplate } from './api/emails';

export const fetchTemplates = async (
  emails: EmailHubTemplate[],
): Promise<SGTemplates> => {
  const fetchedTemplates: SGTemplates = {};

  const fetchPromises = emails.map(async (email: EmailHubTemplate) => {
    const { templateId } = email;

    if (!templateId) return;

    const template = await getTemplate(templateId);
    fetchedTemplates[templateId] = template;
  });

  await Promise.all(fetchPromises);

  return fetchedTemplates;
};

export const getAllEmails = (event: Event): EmailHubTemplate[] => {
  if (isEmpty(event)) return [];

  const { eventId, type, attendeeReviewData } = event;
  const isAttendeeReview = attendeeReviewData?.isAttendeeReview ?? false;
  const isBrand = type === 'brand';

  const emails: EmailHubTemplate[] = [
    {
      templateId: isAttendeeReview
        ? EMAILS.CHECK_IN_ATTENDEE_REVIEW
        : isBrand
          ? EMAILS.CHECK_IN_BRAND
          : EMAILS.CHECK_IN_VIDEOGRAPHER,
      title: 'Check-In Welcoming Email',
      description: 'Is sent automatically when an attendee check-in',
      show: true,
      isAutomatic: true,
    },
    {
      templateId: EMAILS.UGC_RECEIVED,
      title: 'UGC Received Email',
      description: 'Is sent automatically when an attendee uploads their UGC',
      show: isBrand,
      isAutomatic: true,
    },
    {
      templateId: EMAILS.REVIEW_VIDEO,
      title: 'Review Video Email',
      description: 'Is sent automatically when all assets are analysed',
      show: isAttendeeReview,
      isAutomatic: true,
    },
    {
      templateId: event.emailsData?.creationTemplateId ?? '',
      title: isAttendeeReview ? 'Follow-Up Email' : 'Video Creation Email',
      description: (
        <>
          Is sent manually from the{' '}
          <Link to={getUrl(URL.VB_SHARE, eventId)}>Share</Link> page after
          videos are generated
        </>
      ),
      selectionUrl: getUrl(URL.PROJECT_SETTINGS, eventId, 'email-hub/creation'),
      show: true,
    },
    {
      templateId: EMAILS.MISSING_PHOTOS,
      title: 'Missing Photos Email',
      description: (
        <>
          Is sent manually from the{' '}
          <Link to={getUrl(URL.PROJECT_MISSING_ASSETS, eventId)}>
            Missing Assets
          </Link>{' '}
          page to ask attendees with missing photos to upload it
        </>
      ),
      show: !isAttendeeReview,
      isOptional: true,
    },
    {
      templateId: EMAILS.MISSING_CLIPS,
      title: 'Missing Clips Email',
      description: (
        <>
          Is sent manually from the{' '}
          <Link to={getUrl(URL.PROJECT_MISSING_ASSETS, eventId)}>
            Missing Assets
          </Link>{' '}
          page to ask attendees with missing clips to fill in the gaps with
          their videos
        </>
      ),
      show: !isAttendeeReview,
      isOptional: true,
    },
  ];

  return emails;
};

export const getSelectionEmails = (type?: string): EmailHubTemplate[] => {
  const creationTemplates: EmailHubTemplate[] = [
    {
      templateId: EMAILS.CREATION_SHARE,
      title: 'Share Only',
      description: 'Encourage your attendees to just share their video',
    },
    {
      templateId: EMAILS.CREATION_AWARENESS,
      title: 'Spread Awareness',
      description:
        'Encourage your attendees to share their video and spread awareness of your organisation',
    },
    {
      templateId: EMAILS.CREATION_REMOVE_WATERMARK,
      title: 'Remove Watermark & Share',
      description:
        'Use if opted-in for attendees to pay to remove watermark from their videos',
    },
    {
      templateId: EMAILS.CREATION_DONATE,
      title: 'Share & Donate',
      description: 'Use if opted-in for donations from attendees',
    },
    {
      templateId: EMAILS.CREATION_CUSTOM,
      title: 'Custom Text',
      description: 'Customise the email text to your needs',
      isCustom: true,
    },
  ];

  if (type === 'creation') return creationTemplates;
  return [];
};

export const updateEmailText = (props: {
  text: string;
  projectName: string;
  userName?: string;
  customText?: string;
  footer?: string;
}): string => {
  const { text, projectName, userName, customText, footer } = props;

  if (isEmpty(text)) return '';

  return (
    text
      // replace event and user names
      .replace(/{{{event_name}}}/g, projectName)
      .replace(
        /{{{insert name 'default=Amazing human'}}}/g,
        userName ?? 'Amazing human',
      )

      // replace any URLs with CrowdClip website
      .replace(/{{{share_url}}}/g, 'https://www.crowdclip.com/')

      // insert custom text if provided
      .replace(/{{{text}}}/g, customText || '{{{text}}}')

      // add footer into email body
      .replace('</body>', `${footer ?? ''}</body>`)

      // strip problematic styles from SendGrid HTML
      .replace(/style="[^"]*height\s*:\s*100%[^"]*"/g, '')
      .replace(/style="[^"]*overflow\s*:\s*hidden[^"]*"/g, '')
      .replace(/<\/?html[^>]*>/g, '')
      .replace(/<\/?body[^>]*>/g, '')
      .replace(/<\/?head[^>]*>/g, '')
  );
};
